import React from 'react'
import { UseFormReturn } from 'react-hook-form'
import { FormSectionCard } from "./FormSectionCard"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Button } from "@/components/ui/button"
import { DebouncedInput } from "@/components/ui/debounced-input"
import { ClientProfileSelect } from "@/components/ui/profile-selects"
import { CcEmailInput } from "@/components/ui/cc-email-input"
import { FileText, Plus, X } from "lucide-react"
import { useLanguage } from '@/contexts/language-context'
import { useFormSectionNavigation } from '@/hooks/use-form-section-navigation'
import { useCodeValues } from '@/hooks/use-code-values'
import { useAlternateBills } from '@/hooks/use-alternate-bills'
import { InvoiceFormData } from '@/lib/schemas'
import { ENGAGEMENT_BILLING_TYPE } from '@/lib/constants/engagement-codes'
import type { WorkerProfileSummary } from '@/types/api/engagement'

// Constants for select option values
const NONE_VALUE = 'none'

// Delivery Method Constants - only allow these options
const ALLOWED_DELIVERY_METHODS: Record<string, string> = {
  SOFT_COPY: 'SoftCopy',
  SUPPRESSED: 'Suppressed',
}

// Invoice Template Constants
const ASSIGNMENT_ENTITY_TYPE_ID = 8

export interface InvoiceSectionProps {
  form: UseFormReturn<InvoiceFormData>
  selectedClient: number
  isCompleted: boolean
  isReadonly?: boolean
  hiddenSections: Record<string, boolean>
  billingType?: string
  onNoteAdd: () => void
  onNoteRemove: (index: number) => void
  /** Server-resolved fallback names so an already-assigned invoice recipient renders for a restricted caller. */
  profileSummaries?: Record<number, WorkerProfileSummary>
}

export const InvoiceSection = ({
  form,
  selectedClient,
  isCompleted,
  isReadonly = false,
  hiddenSections,
  billingType,
  onNoteAdd,
  onNoteRemove,
  profileSummaries,
}: InvoiceSectionProps) => {
  // Watch invoice notes directly for proper reactivity (same pattern as TimesheetApprovers)
  const invoiceNotes = form.watch('invoiceNotes') || []
  const selectedDeliveryMethod = form.watch('selectedDeliveryMethod')
  const { dictionary } = useLanguage()
  const navigationProps = useFormSectionNavigation('invoice', hiddenSections)
  const { getCodeValues: getByCategory, getRelatedCodeValues } = useCodeValues()
  const isSoftCopy = selectedDeliveryMethod === ALLOWED_DELIVERY_METHODS.SOFT_COPY

  // Get alternate bills for the selected client organization
  const {
    alternateBills,
    loading: loadingAlternateBills
  } = useAlternateBills({ organizationId: selectedClient })

  return (
    <FormSectionCard
      id="invoice"
      title={dictionary?.engagement?.invoice?.title || "Invoice"}
      description={dictionary?.engagement?.invoice?.description || "Manage invoice settings"}
      icon={FileText}
      isCompleted={isCompleted}
      showPrevNext={true}
      {...navigationProps}
    >
      <Form {...form}>
        {/* Alternate Bill */}
        <FormField
          control={form.control}
          name="selectedAlternateBill"
          render={({ field }) => (
            <FormItem className="space-y-2.5">
              <FormLabel className="text-sm font-medium">
                {dictionary?.engagement?.invoice?.alternateBill || "Alternate Bill"}
              </FormLabel>
              <FormControl>
                  <Select
                    value={field.value ?? NONE_VALUE}
                    onValueChange={(value) => field.onChange(value === NONE_VALUE ? null : value)}
                    disabled={isReadonly || !selectedClient || loadingAlternateBills}
                  >
                    <SelectTrigger className="form-field-focus">
                      <SelectValue
                        placeholder={
                          loadingAlternateBills ? "Loading alternate bills..." :
                          alternateBills.length === 0 ? "No alternate bills available" :
                          (dictionary?.engagement?.invoice?.selectAlternateBill || "Select alternate bill (optional)")
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE_VALUE}>None</SelectItem>
                      {alternateBills.map(bill => (
                        <SelectItem key={bill.Id} value={bill.AlternateBillCode}>
                          {bill.AlternateBillLegalName} ({bill.AlternateBillCode})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Billing Frequency - hidden for milestone-based engagements */}
        {billingType !== ENGAGEMENT_BILLING_TYPE.MILESTONE && (
          <FormField
            control={form.control}
            name="selectedBillingFrequency"
            render={({ field }) => (
              <FormItem className="space-y-2.5">
                <FormLabel className="text-sm font-medium">
                  {dictionary?.engagement?.invoice?.billingFrequency || "Billing Frequency"}
                </FormLabel>
                <FormControl>
                  <Select 
                    {...field} 
                    onValueChange={field.onChange} 
                    disabled={isReadonly}
                    allowClear
                  >
                    <SelectTrigger className="form-field-focus">
                      <SelectValue
                        placeholder={
                          dictionary?.engagement?.invoice?.selectBillingFrequency || "Select billing frequency"
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {getByCategory('workorder.CodeBillingFrequency').map(freq => (
                        <SelectItem key={freq.code} value={freq.code!}>
                          {freq.text}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {/* Payment Terms */}
        <FormField
          control={form.control}
          name="selectedPaymentTerms"
          render={({ field }) => (
            <FormItem className="space-y-2.5">
              <FormLabel className="text-sm font-medium">
                {dictionary?.engagement?.invoice?.paymentTerms || "Payment Terms"}
              </FormLabel>
              <FormControl>
                <Select
                  {...field}
                  onValueChange={field.onChange}
                  disabled={isReadonly}
                  allowClear
                >
                  <SelectTrigger className="form-field-focus">
                    <SelectValue
                      placeholder={dictionary?.engagement?.invoice?.selectPaymentTerms || "Select payment terms"}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {getByCategory('workorder.CodeBillingInvoiceTerms').map(terms => (
                      <SelectItem key={terms.code} value={terms.code!}>
                        {terms.text}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Invoice Template */}
        <FormField
          control={form.control}
          name="selectedInvoiceTemplate"
          render={({ field }) => (
            <FormItem className="space-y-2.5">
              <FormLabel className="text-sm font-medium">
                {dictionary?.engagement?.invoice?.invoiceTemplate || "Invoice Template"}
              </FormLabel>
              <FormControl>
                <Select
                  {...field}
                  onValueChange={field.onChange}
                  disabled={isReadonly}
                  allowClear
                >
                  <SelectTrigger className="form-field-focus">
                    <SelectValue
                      placeholder={
                        dictionary?.engagement?.invoice?.selectInvoiceTemplate || "Select invoice template"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {getRelatedCodeValues(
                      'workorder.CodeBillingInvoiceTemplate',
                      ASSIGNMENT_ENTITY_TYPE_ID,
                      'app.CodeEntityType'
                    ).map(template => (
                      <SelectItem key={template.code} value={template.code}>
                        {template.text}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Delivery Method */}
        <FormField
          control={form.control}
          name="selectedDeliveryMethod"
          render={({ field }) => (
            <FormItem className="space-y-2.5">
              <FormLabel className="text-sm font-medium">
                {dictionary?.engagement?.invoice?.deliveryMethod || "Delivery Method"}
              </FormLabel>
              <FormControl>
                <Select
                  {...field}
                  onValueChange={field.onChange}
                  disabled={isReadonly}
                  allowClear
                >
                  <SelectTrigger className="form-field-focus">
                    <SelectValue
                      placeholder={
                        dictionary?.engagement?.invoice?.selectDeliveryMethod || "Select delivery method"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {getByCategory('workorder.CodeDeliveryMethod')
                      .filter(method => Object.values(ALLOWED_DELIVERY_METHODS).includes(method.code))
                      .map(method => (
                      <SelectItem key={method.code} value={method.code}>
                        {method.text}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Invoice Recipient */}
        <FormField
          control={form.control}
          name="selectedInvoiceRecipient"
          render={({ field }) => (
            <FormItem className="space-y-2.5">
              <FormLabel className="text-sm font-medium">
                {dictionary?.engagement?.invoice?.invoiceRecipient || "Invoice Recipient"}
              </FormLabel>
              <FormControl>
                <ClientProfileSelect
                  {...field}
                  className="form-field-focus"
                  placeholder={dictionary?.engagement?.invoice?.selectInvoiceRecipient || "Select invoice recipient"}
                  selectedClient={selectedClient}
                  isReadonly={isReadonly}
                  profileSummaries={profileSummaries}
                  allowClear
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* CC Emails - only visible when Delivery Method is Soft Copy */}
        {isSoftCopy && (
          <FormField
            control={form.control}
            name="ccEmails"
            render={({ field, fieldState }) => (
              <FormItem className="space-y-2.5">
                <FormLabel className="text-sm font-medium">
                  CC Emails
                </FormLabel>
                <FormControl>
                  <CcEmailInput
                    value={field.value}
                    onChange={field.onChange}
                    disabled={isReadonly}
                    isReadonly={isReadonly}
                    placeholder="Enter email and press Enter, comma, or space"
                  />
                </FormControl>
                {fieldState.error && (
                  <p className="text-sm font-medium text-amber-600 dark:text-amber-400">
                    {fieldState.error.message}
                  </p>
                )}
              </FormItem>
            )}
          />
        )}

        {/* Invoice Notes */}
        <div className="space-y-4">
          {/* Section Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium">
                {dictionary?.engagement?.invoice?.invoiceNotes || "Invoice Notes"}
              </label>
              <span className="text-xs text-muted-foreground">
                ({invoiceNotes.length}/4)
              </span>
            </div>
            {!isReadonly && invoiceNotes.length > 0 && invoiceNotes.length < 4 && (
              <Button
                type="button"
                onClick={onNoteAdd}
                variant="outline"
                size="sm"
                className="h-8 gap-1.5 text-xs bg-transparent"
              >
                <Plus className="h-3.5 w-3.5" />
                {dictionary?.engagement?.invoice?.addNote || "Add Note"}
              </Button>
            )}
          </div>

          {invoiceNotes.length === 0 ? (
            /* Empty State */
            <div className="rounded-lg border border-border bg-muted/50 px-4 py-8 text-center">
              {isReadonly ? (
                <p className="text-sm text-muted-foreground">
                  {dictionary?.engagement?.invoice?.noNotes || "No notes"}
                </p>
              ) : (
                <>
                  <p className="mb-4 text-sm text-muted-foreground">
                    {dictionary?.engagement?.invoice?.noNotesYet || "No notes yet. Add your first note!"}
                  </p>
                  <Button
                    type="button"
                    onClick={onNoteAdd}
                    variant="outline"
                    size="sm"
                    className="gap-1.5 bg-transparent"
                  >
                    <Plus className="h-4 w-4" />
                    {dictionary?.engagement?.invoice?.addNote || "Add Note"}
                  </Button>
                </>
              )}
            </div>
          ) : (
            /* Notes List */
            <div className="space-y-3">
              {invoiceNotes.map((note, index) => (
                <FormField
                  key={index}
                  control={form.control}
                  name={`invoiceNotes.${index}.value`}
                  render={({ field: inputField }) => (
                    <FormItem className="space-y-2">
                      <FormLabel className="text-sm font-medium text-foreground/70">
                        Invoice Note {index + 1}
                      </FormLabel>
                      <div className="flex items-center gap-3">
                        <FormControl className="flex-1">
                          <DebouncedInput
                            {...inputField}
                            type="text"
                            placeholder={dictionary?.engagement?.invoice?.notePlaceholder || `Enter note ${index + 1}`}
                            readOnly={isReadonly}
                            className="form-field-focus"
                          />
                        </FormControl>
                        {!isReadonly && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => onNoteRemove(index)}
                            className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
                            aria-label={`Remove note ${index + 1}`}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ))}
            </div>
          )}
        </div>

        {/* Include Timesheet */}
        {/*Hidden for now, since we don't have a mapping for this on the Work Order, so it's not used*/}
        {/* <FormField
          control={form.control}
          name="includeTimesheet"
          render={({ field }) => (
            <FormItem className="space-y-2.5 flex items-center justify-between">
              <FormLabel className="text-sm font-medium">
                {dictionary?.engagement?.invoice?.includeTimesheet || "Include Timesheet"}
              </FormLabel>
              <FormControl>
                <Switch 
                  checked={field.value} 
                  onCheckedChange={field.onChange}
                  disabled={isReadonly}
                />
              </FormControl>
            </FormItem>
          )}
        /> */}
      </Form>
    </FormSectionCard>
  )
}

export default InvoiceSection